import * as React from 'react';

import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import AdminSisSyncStatusLabel from 'admin/rostering/components/AdminSisSyncStatusLabel';
import Button from 'components/ButtonDeprecated';
import CurrentUser from 'auth/CurrentUser';
import Link from 'components/Link';
import Text from 'components/Text';
import SyncTypePickerConstants from 'constants/SyncTypePickerConstants';
import TitleHeader from 'components/TitleHeader';
import { SyncType } from 'globalTypes';
import {
  AdminDistrictSisDashboard_organizations_childOrganizations as ChildOrganization,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus as DistrictStatus,
  AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus_schoolMappings as SchoolMapping,
} from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

type Props = {
  childOrganizations: ChildOrganization[];
  districtStatus: DistrictStatus;
  orgUuid: string;
  canOnlySeeReviewScreen: boolean;
  canOnlySeeRosteringStagingArea: boolean;
};

export default class AdminDistrictSisDashboardSftpHeader extends React.Component<Props> {
  numSchoolsToMap = (schoolMappings: SchoolMapping[], childOrgs: ChildOrganization[]) => {
    const numPaidChildOrgs = childOrgs.filter(org => org.paidAt).length;
    const numMappedChildOrgs = schoolMappings.filter(mapping => mapping.remindSchoolUuid).length;

    return numPaidChildOrgs - numMappedChildOrgs;
  };

  renderMatchingBanner = () => {
    const { orgUuid } = this.props;

    const button = (
      <Button
        eventElementName="AdminDistrictSisDashboardSftpHeader-matchYourSchools"
        href={`/sftp_sync_setup/matching?district_uuid=${orgUuid}`}>
        Match schools
      </Button>
    );

    return (
      <AdminNoticeBanner glyph="success-checkmark-stroke" color="mascot" actionElement={button}>
        Next, confirm that the schools in the CSV files match schools on Remind.
      </AdminNoticeBanner>
    );
  };

  renderAwaitingFilesBanner = () => {
    return (
      <AdminNoticeBanner glyph="clock" color="pencil" actionElement={null}>
        We’re processing your files now. This may take several minutes.
      </AdminNoticeBanner>
    );
  };

  renderAwaitingReviewBanner = () => {
    return (
      <AdminNoticeBanner glyph="clock" color="pencil" actionElement={null}>
        Our implementation team is reviewing your data. You'll receive an email with next steps in
        two business days.
      </AdminNoticeBanner>
    );
  };

  renderStagingEnvironmentBanner = () => {
    return (
      <AdminNoticeBanner glyph="clock" color="percussion" actionElement={null}>
        Review each school's sync to fix errors. When you're ready to push data live, contact our
        implementation team.
      </AdminNoticeBanner>
    );
  };

  renderNewCsvValidationsSubtitle = (syncType: SyncType, waitingOnAction: boolean) => {
    if (waitingOnAction) {
      const syncTypeText = this.getSyncTypeText(syncType);
      const syncTypeLink = this.getSyncTypeLink(syncType);

      return (
        <Text>
          Format your school data in the{' '}
          <Link target="_blank" href={syncTypeLink} weight="semibold">
            {syncTypeText} format
          </Link>
          , then transfer the files using the SFTP credentials below.{' '}
        </Text>
      );
    }
  };

  getSyncTypeText = (syncType: SyncType): string => {
    switch (syncType) {
      case SyncType.oneRosterSpreadsheet:
        return 'OneRoster CSV';
      case SyncType.remindSpreadsheet:
        return 'Remind CSV 1.0';
      case SyncType.remindSpreadsheetV2:
        return 'Remind CSV 2.0';
      default:
        return '';
    }
  };

  getSyncTypeLink = (syncType: SyncType): string => {
    switch (syncType) {
      case SyncType.oneRosterSpreadsheet:
        return SyncTypePickerConstants.ONEROSTER.textLink;
      case SyncType.remindSpreadsheet:
        return SyncTypePickerConstants.REMIND.textLink;
      case SyncType.remindSpreadsheetV2:
        return SyncTypePickerConstants.REMIND2.textLink;
      default:
        return '';
    }
  };

  renderBanner = (someSchoolsNotMapped: boolean, noSchoolsMapped: boolean) => {
    const { districtStatus, canOnlySeeRosteringStagingArea, canOnlySeeReviewScreen } = this.props;
    const { awaitingFiles, filesZipUrl } = districtStatus;

    if (awaitingFiles) {
      return this.renderAwaitingFilesBanner();
    } else if (noSchoolsMapped) {
    } else if (someSchoolsNotMapped) {
      return this.renderMatchingBanner();
    } else if (canOnlySeeReviewScreen) {
      return this.renderAwaitingReviewBanner();
    } else if (canOnlySeeRosteringStagingArea) {
      return this.renderStagingEnvironmentBanner();
    } else {
      return null;
    }
  };

  renderTitle = (isNotActive: boolean) => {
    if (isNotActive) {
      return <AdminSisSyncStatusLabel status="pending" />;
    }
    return <AdminSisSyncStatusLabel status="active" />;
  };

  render() {
    const {
      districtStatus,
      childOrganizations,
      orgUuid,
      canOnlySeeReviewScreen,
      canOnlySeeRosteringStagingArea,
    } = this.props;

    const { awaitingFiles, filesZipUrl, schoolMappings } = districtStatus;

    const noSchoolsMapped = schoolMappings.length === 0;
    const someSchoolsNotMapped = this.numSchoolsToMap(schoolMappings, childOrganizations) > 0;
    const isNotActive =
      awaitingFiles || noSchoolsMapped || canOnlySeeReviewScreen || canOnlySeeRosteringStagingArea;
    const waitingOnAction = awaitingFiles || noSchoolsMapped || someSchoolsNotMapped;

    return (
      <TitleHeader
        title={this.renderTitle(isNotActive)}
        subtext={this.renderNewCsvValidationsSubtitle(
          districtStatus.syncType as SyncType,
          waitingOnAction
        )}
        actionPlacement="right"
        className="AdminDistrictSisDashboardSftpHeader">
        {this.renderBanner(someSchoolsNotMapped, noSchoolsMapped)}
      </TitleHeader>
    );
  }
}
